import React from 'react'
import styles from './connector.module.scss'
import { PositionedBracket } from 'core/models/Bracket'

interface ConnectorProps {
  brackets: PositionedBracket[]
  bracketWidth: number
  bracketHeight: number
  color?: string
  strokeWidth?: number
  joinGap?: number
  className?: string
  hoveredFromId?: string
  hoveredFromIndex?: 0 | 1
  connectorHighlightColor?: string
}

export const Connector: React.FC<ConnectorProps> = ({
  brackets,
  bracketWidth,
  bracketHeight,
  color = '#7aa9ff',
  strokeWidth = 2,
  joinGap = 30,
  className,
  hoveredFromId,
  hoveredFromIndex,
  connectorHighlightColor = '#00e1ff',
}) => {
  const map = new Map(brackets.map((b) => [b.id, b]))

  const teamKeyOf = (t: any): string | null => {
    if (!t) return null
    return t.id ?? t._id ?? t.uuid ?? t.teamId ?? t.slug ?? t.name ?? null
  }

  let hoveredTeamKey: string | null = null
  if (hoveredFromId && (hoveredFromIndex === 0 || hoveredFromIndex === 1)) {
    const src = map.get(hoveredFromId)
    const team = src?.seed?.teams?.[hoveredFromIndex]
    hoveredTeamKey = teamKeyOf(team)
  }

  const teamInMatch = (matchId: string, key: string | null): boolean => {
    if (!key) return false
    const m = map.get(matchId)
    const teams = m?.seed?.teams || []
    return teams.some((t: any) => teamKeyOf(t) === key)
  }

  const isEdgeHighlighted = (srcId: string, destId: string): boolean => {
    if (!hoveredTeamKey) return false
    return (
      teamInMatch(srcId, hoveredTeamKey) && teamInMatch(destId, hoveredTeamKey)
    )
  }

  const elements: JSX.Element[] = []

  for (const dest of brackets) {
    const deps = (dest.seed.dependencies || [])
      .map((d: any) => map.get(d.sourceBracketId))
      .filter((s): s is PositionedBracket => !!s)
      .filter((src) => {
        if (src.region === 'third' || dest.region === 'third') return false
        if (dest.region === 'lower' && src.region !== 'lower') return false
        if (src.region === dest.region) return true
        if (dest.region === 'final') return true
        return false
      })

    if (!deps.length) continue

    const destCenterY = dest.y + bracketHeight / 2
    const joinX = dest.x - joinGap

    if (deps.length === 1) {
      const src = deps[0]
      const x1 = src.x + bracketWidth
      const y1 = src.y + bracketHeight / 2

      const edgeHighlighted = isEdgeHighlighted(src.id, dest.id)
      const cls = `${styles.segment} ${styles.single} ${edgeHighlighted ? styles.highlighted : ''}`

      if (Math.abs(y1 - destCenterY) < 0.5) {
        elements.push(
          <path
            key={`${src.id}->${dest.id}`}
            className={cls}
            d={`M ${x1} ${y1} H ${dest.x}`}
            data-src-id={src.id}
            data-dest-id={dest.id}
          />
        )
      } else {
        elements.push(
          <path
            key={`${src.id}->${dest.id}`}
            className={cls}
            d={`M ${x1} ${y1} H ${joinX} V ${destCenterY} H ${dest.x}`}
            data-src-id={src.id}
            data-dest-id={dest.id}
          />
        )
      }
      continue
    }

    const sources = deps.map((s) => ({
      id: s.id,
      x: s.x + bracketWidth,
      y: s.y + bracketHeight / 2,
      edgeHighlighted: isEdgeHighlighted(s.id, dest.id),
    }))

    const yMinSources = Math.min(...sources.map((s) => s.y))
    const yMaxSources = Math.max(...sources.map((s) => s.y))
    const trunkTop = Math.min(yMinSources, destCenterY)
    const trunkBottom = Math.max(yMaxSources, destCenterY)

    elements.push(
      <path
        key={`trunk-${dest.id}`}
        className={`${styles.segment} ${styles.trunk}`}
        d={`M ${joinX} ${trunkTop} V ${trunkBottom}`}
        data-dest-id={dest.id}
      />
    )

    for (const s of sources) {
      elements.push(
        <path
          key={`${s.id}->${dest.id}-h`}
          className={`${styles.segment} ${styles.source} ${s.edgeHighlighted ? styles.highlighted : ''}`}
          d={`M ${s.x} ${s.y} H ${joinX}`}
          data-src-id={s.id}
          data-dest-id={dest.id}
        />
      )
    }

    for (const s of sources) {
      if (!s.edgeHighlighted) continue
      if (Math.abs(s.y - destCenterY) < 0.5) continue
      elements.push(
        <path
          key={`${s.id}->${dest.id}-v`}
          className={`${styles.segment} ${styles.branch} ${styles.highlighted}`}
          d={`M ${joinX} ${s.y} V ${destCenterY}`}
          data-src-id={s.id}
          data-dest-id={dest.id}
        />
      )
    }

    const intoHighlighted = sources.some((s) => s.edgeHighlighted)
    elements.push(
      <path
        key={`into-${dest.id}`}
        className={`${styles.segment} ${styles.into} ${intoHighlighted ? styles.highlighted : ''}`}
        d={`M ${joinX} ${destCenterY} H ${dest.x}`}
        data-dest-id={dest.id}
      />
    )
  }

  return (
    <g
      className={`${styles.group} ${className || ''}`}
      style={
        {
          '--connector-color': color,
          '--connector-width': strokeWidth,
          '--connector-highlight-color': connectorHighlightColor,
        } as React.CSSProperties
      }
    >
      {elements}
    </g>
  )
}
